import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, toastError } from "@/lib/api";
import { todayISO, addDaysISO } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, ChevronDown, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { dayLabel } from "./AccommodationBits";

const F = ({ label, children, hint, className }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label className="text-[12px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11.5px] leading-4 text-muted-foreground">{hint}</p>}
  </div>
);

/** Pick a free bed for the period – grouped by building, current room first when given. */
const BedPicker = ({ projectId, start, end, excludeStayId, value, onChange, preferRoomId }) => {
  const ok = Boolean(projectId && start && end && end > start);
  const q = useQuery({ queryKey: ["free-beds", projectId, start, end, excludeStayId], queryFn: () => get(`/innkvartering/projects/${projectId}/free-beds`, { start, end, exclude_stay_id: excludeStayId || undefined }), enabled: ok });
  const beds = q.data || [];
  const sorted = useMemo(() => [...beds].sort((a, b) => (a.room_id === preferRoomId ? -1 : b.room_id === preferRoomId ? 1 : 0) || String(a.operational_number).localeCompare(String(b.operational_number))), [beds, preferRoomId]);
  const key = (b) => `${b.room_id}:${b.bed_index}`;
  return (
    <F label="Rom og seng" hint={ok ? (q.isLoading ? "Finner ledige senger…" : `${beds.length} ledige senger i perioden`) : "Velg datoer først"}>
      <Select value={value || ""} onValueChange={onChange} disabled={!ok || q.isLoading}>
        <SelectTrigger data-testid="stay-bed-select"><SelectValue placeholder={q.isLoading ? "Laster…" : "Velg seng"} /></SelectTrigger>
        <SelectContent className="max-h-72">
          {sorted.map((b) => (
            <SelectItem key={key(b)} value={key(b)} data-testid={`stay-bed-option-${b.room_id}-${b.bed_index}`}>
              <span className="font-semibold tabular">{b.operational_number}</span>
              {b.beds > 1 && <span className="text-muted-foreground"> · seng {b.bed_index}</span>}
              <span className="ml-2 font-mono text-[11px] text-muted-foreground">{b.physical_code}</span>
              {b.room_status === "ma_rengjores" && <span className="ml-2 text-[11px] text-amber-700">må rengjøres</span>}
            </SelectItem>
          ))}
          {ok && !q.isLoading && !sorted.length && <div className="px-2 py-3 text-[12.5px] text-muted-foreground">Ingen ledige senger i perioden.</div>}
        </SelectContent>
      </Select>
    </F>
  );
};

/**
 * Register a stay (existing person or quick-create) or move a stay to another bed.
 * Everything is validated server-side again (bed conflicts, room status, scope).
 */
export const StaySheet = ({ open, onOpenChange, projectId, mode = "create", room, stay, initial, persons = [], crews = [], onDone }) => {
  const qc = useQueryClient();
  const today = todayISO();
  const [personMode, setPersonMode] = useState("existing");
  const [f, setF] = useState({});
  const [np, setNp] = useState({});

  useEffect(() => {
    if (!open) return;
    if (mode === "move" && stay) {
      setF({ planned_in: stay.status === "innsjekket" ? today : stay.planned_in, planned_out: stay.planned_out, bed: "", reason: "" });
    } else {
      // `initial` comes from the room calendar (drag on an empty lane): bed + period are pre-filled, still validated server-side
      const pin = initial?.planned_in || today;
      setF({ person_id: "", crew_id: room?.upcoming?.[0]?.crew_id || "", planned_in: pin, planned_out: initial?.planned_out || addDaysISO(pin, 14), bed: initial?.room_id ? `${initial.room_id}:${initial.bed_index || 1}` : room ? `${room.room_id}:1` : "", check_in_now: pin === today });
      setNp({ name: "", employer_name: "", role: "", phone: "", ec_name: "", ec_phone: "", ec_relation: "" });
      setPersonMode(persons.length ? "existing" : "new");
    }
  }, [open, mode, stay?.id, room?.room_id, initial?.room_id, initial?.bed_index, initial?.planned_in, initial?.planned_out]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const available = useMemo(() => persons.filter((p) => !p.anonymized_at && (!p.current_stay || p.current_stay.status !== "innsjekket")), [persons]);
  const person = persons.find((p) => p.id === f.person_id);
  const [roomId, bedIndex] = (f.bed || "").split(":");
  const canCheckInNow = f.planned_in === today;

  const m = useMutation({
    mutationFn: async () => {
      if (mode === "move") return post(`/innkvartering/stays/${stay.id}/move`, { room_id: roomId, bed_index: Number(bedIndex || 1), reason: f.reason || null });
      const body = { project_id: projectId, room_id: roomId, bed_index: Number(bedIndex || 1), planned_in: f.planned_in, planned_out: f.planned_out, crew_id: f.crew_id || person?.crew_id || null, check_in_now: canCheckInNow && f.check_in_now };
      if (personMode === "existing") body.person_id = f.person_id;
      else body.person = { project_id: projectId, name: np.name, employer_name: np.employer_name || null, role: np.role || null, phone: np.phone || null, crew_id: f.crew_id || null, emergency_contact: np.ec_name || np.ec_phone ? { name: np.ec_name || null, phone: np.ec_phone || null, relation: np.ec_relation || null } : null };
      return post("/innkvartering/stays", body);
    },
    onSuccess: (d) => {
      toast.success(mode === "move" ? `Flyttet til ${d.operational_number}` : d.status === "innsjekket" ? `${d.person_name} sjekket inn i ${d.operational_number}` : `Opphold planlagt for ${d.person_name} i ${d.operational_number}`);
      qc.invalidateQueries({ queryKey: ["ink"] });
      onDone?.(d);
      onOpenChange(false);
    },
    onError: (e) => toastError(e, "Kunne ikke lagre oppholdet"),
  });

  const valid = roomId && f.planned_in && f.planned_out && f.planned_out > f.planned_in && (mode === "move" || (personMode === "existing" ? Boolean(f.person_id) : Boolean(np.name?.trim())));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-[480px] max-w-[94vw] flex-col gap-0 overflow-y-auto p-0" data-testid="stay-sheet">
        <SheetHeader className="border-b px-6 py-5 text-left">
          <SheetTitle className="text-[16px]">{mode === "move" ? `Flytt ${stay?.person_name || "person"}` : "Legg inn opphold"}</SheetTitle>
          <SheetDescription className="text-[12.5px]">{mode === "move" ? `Fra ${stay?.operational_number} · seng ${stay?.bed_index || 1}. Velg ny seng for resten av oppholdet.` : "Minimal registrering: navn, arbeidsgiver, telefon og nødkontakt. Ingen fødselsnummer eller helseopplysninger."}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 px-6 py-5">
          {mode === "create" && (
            <>
              <div className="flex gap-1 rounded-md border bg-muted/40 p-0.5 text-[12.5px]" role="tablist">
                {[["existing", "Registrert person"], ["new", "Ny person"]].map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={personMode === k} onClick={() => setPersonMode(k)} className={cn("flex-1 rounded px-2 py-1.5 transition-colors duration-150", personMode === k ? "bg-background font-medium shadow-sm" : "text-muted-foreground hover:text-foreground")} data-testid={`stay-person-mode-${k}`}>{l}</button>
                ))}
              </div>
              {personMode === "existing" ? (
                <F label="Person" hint={available.length ? `${available.length} registrerte uten aktivt opphold` : "Ingen registrerte personer uten opphold – legg inn ny person."}>
                  <Select value={f.person_id || ""} onValueChange={(v) => { set("person_id", v); const p = persons.find((x) => x.id === v); if (p?.crew_id) set("crew_id", p.crew_id); }}>
                    <SelectTrigger data-testid="stay-person-select"><SelectValue placeholder="Velg person" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {available.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`stay-person-option-${p.id}`}>{p.name} <span className="text-muted-foreground">· {p.employer_name}{p.crew_name ? ` · ${p.crew_name}` : ""}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
              ) : (
                <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                  <div className="flex items-center gap-2 text-[12.5px] font-medium"><UserPlus className="h-3.5 w-3.5" /> Ny person</div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Navn" className="col-span-2"><Input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Fornavn Etternavn" data-testid="stay-new-person-name" /></F>
                    <F label="Arbeidsgiver"><Input value={np.employer_name} onChange={(e) => setNp({ ...np, employer_name: e.target.value })} data-testid="stay-new-person-employer" /></F>
                    <F label="Rolle"><Input value={np.role} onChange={(e) => setNp({ ...np, role: e.target.value })} placeholder="Montør" data-testid="stay-new-person-role" /></F>
                    <F label="Telefon" className="col-span-2"><Input value={np.phone} onChange={(e) => setNp({ ...np, phone: e.target.value })} placeholder="+47 …" data-testid="stay-new-person-phone" /></F>
                  </div>
                  <Collapsible>
                    <CollapsibleTrigger className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground" data-testid="stay-new-person-ec-toggle">Nødkontakt (valgfritt) <ChevronDown className="h-3.5 w-3.5" /></CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 grid grid-cols-2 gap-3">
                      <F label="Navn"><Input value={np.ec_name} onChange={(e) => setNp({ ...np, ec_name: e.target.value })} data-testid="stay-new-person-ec-name" /></F>
                      <F label="Relasjon"><Input value={np.ec_relation} onChange={(e) => setNp({ ...np, ec_relation: e.target.value })} placeholder="Ektefelle" data-testid="stay-new-person-ec-relation" /></F>
                      <F label="Telefon" className="col-span-2"><Input value={np.ec_phone} onChange={(e) => setNp({ ...np, ec_phone: e.target.value })} data-testid="stay-new-person-ec-phone" /></F>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
              {crews.length > 0 && (
                <F label="Crew" hint="Crewet styrer rotasjonen og forventet belegg.">
                  <Select value={f.crew_id || "none"} onValueChange={(v) => set("crew_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="stay-crew-select"><SelectValue placeholder="Uten crew" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Uten crew</SelectItem>{crews.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-muted-foreground">· {c.employer_name}</span></SelectItem>)}</SelectContent>
                  </Select>
                </F>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label={mode === "move" ? "Fra" : "Innsjekk"}>
              <Input type="date" value={f.planned_in || ""} onChange={(e) => set("planned_in", e.target.value)} disabled={mode === "move"} data-testid="stay-planned-in" />
            </F>
            <F label="Utsjekk">
              <Input type="date" value={f.planned_out || ""} onChange={(e) => set("planned_out", e.target.value)} min={f.planned_in} disabled={mode === "move"} data-testid="stay-planned-out" />
            </F>
          </div>
          <BedPicker projectId={projectId} start={f.planned_in} end={f.planned_out} excludeStayId={stay?.id} value={f.bed} onChange={(v) => set("bed", v)} preferRoomId={room?.room_id || stay?.room_id || initial?.room_id} />
          {mode === "move" ? (
            <F label="Årsak (valgfritt)"><Input value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} placeholder="F.eks. støy, defekt bad, ønsket samlokalisering" data-testid="stay-move-reason" /></F>
          ) : (
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <div>
                <div className="text-[13px] font-medium">Sjekk inn nå</div>
                <div className="text-[11.5px] text-muted-foreground">{canCheckInNow ? "Personen registreres som innsjekket i dag." : `Kun mulig når innsjekk er i dag (planlagt ${dayLabel(f.planned_in)}).`}</div>
              </div>
              <Switch checked={canCheckInNow && Boolean(f.check_in_now)} onCheckedChange={(v) => set("check_in_now", v)} disabled={!canCheckInNow} data-testid="stay-check-in-now-switch" />
            </div>
          )}
        </div>
        <div className="mt-auto flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="stay-cancel-button">Avbryt</Button>
          <Button onClick={() => m.mutate()} disabled={!valid || m.isPending} data-testid="stay-submit-button">
            {m.isPending && <Loader2 className="animate-spin" />} {mode === "move" ? "Flytt" : canCheckInNow && f.check_in_now ? "Sjekk inn" : "Planlegg opphold"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
